/**
 * useAutonomyGates (J6, Wave 3) — fetch-on-mount + manual retry for the
 * G1–G10 activation gates board. Deliberately a SEPARATE hook with its own
 * error state (AC-EH2): a gates-endpoint failure renders check-unavailable
 * rows with Retry while the settings half of the page stays fully usable —
 * the page never blanks.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { autonomyClient } from '../api/autonomyClient';
import type { AutonomyGatesData } from '../types/gates.types';

export interface UseAutonomyGatesResult {
  data: AutonomyGatesData | null;
  /** True only for the initial load (no data yet). */
  loading: boolean;
  /** True while a refetch is in flight and stale data is still on screen. */
  refreshing: boolean;
  /** Independent of the settings hook — a gates failure never blocks levers. */
  error: Error | null;
  /** Wired to the board's RETRY affordance. */
  refetch: () => Promise<void>;
}

export function useAutonomyGates(projectId: string | undefined): UseAutonomyGatesResult {
  const [data, setData] = useState<AutonomyGatesData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await autonomyClient.getGates(projectId);
      if (seq !== requestSeq.current) return; // stale response — a newer request superseded it
      setData(fresh);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    requestSeq.current += 1; // invalidate in-flight requests from a previous project
    setData(null);
    setLoading(true);
    setError(null);
    void refetch();
  }, [refetch]);

  return { data, loading, refreshing, error, refetch };
}
